import React, { useRef } from "react";
import { useParams } from "react-router-dom";
import { Descriptions, Layout, theme } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import ColorButton from "../common/ColorButton";
import TSTable from "../common/TSTable";
import {  URL_SETTING, URL_TRUCK } from "../constants/urls";
import ErrorMessages from "../common/ErrorMessages";
import { Content } from "antd/es/layout/layout";
import HeaderScreen from "../common/HeaderScreen";
import { SECONDARY_COLOR, WHITE_COLOR } from "../constants/color";
import { DELETE, GET } from "../frameworks/HttpClient";
import DispenseChemicalModal from "../components/chemical/DispenseChemicalModal";
import _ from "lodash";
import { dateToHumanString, dateToString } from "../frameworks/Util";
import CustomDeleteConfirmButton from "../common/CustomDeleteConfirmButton";

export default function DispenseChemicalScreen (){
  const params = useParams();
  const [truckData, setTruckData] = React.useState(null);
  const [sellerData, setSellerData] = React.useState(null);
  const [dispenseTarget, setDispenseTarget] = React.useState(null);
  const [errorMessages, setErrorMessages] = React.useState(null);
  const [disabled, setDisabled] = React.useState(false);
  const isTruck = params.type === "truck"
  const tableRef = useRef();

  const fetchTruckData = async() => {
    try {
      if(isTruck){
        const response = await GET(`${URL_TRUCK.TRUCK_TRANSACTION}${params.id}/`,)
        setTruckData(_.get(response, 'data', {}));
      }
    } catch (error) {
      setErrorMessages(error.errorMessages)
    }
  }

  const fetchSellerData = async() => {
    try {
        const response = await GET(`${URL_SETTING.SELLER_RAW_LATEX}${params.id}/`,)
        setSellerData(_.get(response, 'data', {}));
    } catch (error) {
      if(error && error.status === 404){
        setErrorMessages('ไม่พบข้อมูลของผู้ขาย')
        setDisabled(true);
      } else { 
        setErrorMessages(error.errorMessages)
      }
    }
  }

  const {
    token: { colorSuccess, colorError },
  } = theme.useToken();

  const items = [
    {
      key: 'created',
      label: 'วันที่',
      children: dateToHumanString(_.get(truckData, 'created', '-')),
      span : 1
    },
    {
      key: 'saller',
      label: 'ผู้ขาย',
      children: _.get(truckData, 'supplier_name', '-'),
      span : 3
    },
    {
      key: 'licence_plate',
      label: 'ทะเบียนรถ',
      children: _.get(truckData, 'vehicle_license_plate', '-'),
      span: 1
    },
    {
      key: 'latex',
      label: 'ปริมาณน้ำยางสด',
      children: _.get(truckData, 'net_weight', '-'),
      span: 1
    },
    {
      key: 'NH3',
      label: 'NH3',
      children: _.get(truckData, 'lab_results', []).find((e) => e.chemical === 'NH3') ? _.get(truckData, 'lab_results', []).find((e) => e.chemical === 'NH3').amount : '-',
      span : 1
    },
    {
      key: 'TZ',
      label: 'TZ',
      children: _.get(truckData, 'lab_results', []).find((e) => e.chemical === 'TZ') ? _.get(truckData, 'lab_results', []).find((e) => e.chemical === 'TZ').amount : '-',
      span : 1
    },
  ]

  const refreshData = async() => {
    if(tableRef.current)(
      await tableRef.current.fetchWithoutResetPage()
    )
  }

  const handleDelete = async(id) => {
    setErrorMessages(null)
    try {
      await DELETE(`${URL_TRUCK.CHEMICAL_TRANSFER}${id}/`)
    } catch (error) {
      setErrorMessages(error.errorMessages)
    }
  }

  const columns = [
    {
      title: 'Action',
      dataIndex: '',
      width : 100,
      key: 'action',
      render: (_, original) => {
        return (
          <div 
            style={{textAlign:'center'}}
            onClick={(e) => {
              e.stopPropagation()
            }}
          >
            {!disabled && <CustomDeleteConfirmButton
              title={`ลบรายการจ่ายสารเคมี`}
              content={"คุณต้องการลบรายการจ่ายสารเคมีใช่หรือไม่"}
              onOk={ async() => {
                await handleDelete(original.id)
                refreshData();
              }}
              onConfirm={() => refreshData()}
            />}
          </div>
      )}
    },
    {
      title: 'วันที่จ่าย',
      dataIndex: 'date',
      key: 'date',
      dateSearcher: true,  // Search by date selection
    },
    {
      title: 'ผู้ขายน้ำยางสด',
      dataIndex: 'supplier_name',
      key: 'supplier_name',
      searcher: true,
      // render : (value) => {return value}
    },
    {
      title: 'ทะเบียนรถ',
      dataIndex: 'license_plate',
      key: 'license_plate',
      searcher: true,
    },
    {
      title: 'NH4OH',
      children: [
        {
          title: 'Lot No.',
          dataIndex: '',
          key: 'lot_no',
          render: (value, original) => value.transfer_details.find((e) => e.chemical === 'NH4OH') ? value.transfer_details.find((e) => e.chemical === 'NH4OH').stock_lot_no : '-'
        },
        {
          title: 'ปริมาณ (Kg)',
          dataIndex: '',
          key: 'hn30h_weight',
          render: (value, original) => { return value.transfer_details.find((e) => e.chemical === 'NH4OH') ? value.transfer_details.find((e) => e.chemical === 'NH4OH').amount : '-' }
        },
      ],
    },
    {
      title: 'TZ',
      children: [
        {
          title: 'Lot No.',
          dataIndex: '',
          key: 'lot_no',
          render: (value, original) => value.transfer_details.find((e) => e.chemical === 'TZ') ? value.transfer_details.find((e) => e.chemical === 'TZ').stock_lot_no : '-'
        },
        {
          title: 'ปริมาณ (Kg)',
          dataIndex: '',
          key: 'tz_weight',
          render: (value, original) => value.transfer_details.find((e) => e.chemical === 'TZ') ? value.transfer_details.find((e) => e.chemical === 'TZ').amount : '-'
        },
      ],
    },
    {
      title: 'ผู้จ่าย',
      dataIndex: 'created_by_name',
      key: 'created_by_name',
      searcher: true,
    },
  ]

  const handleClose = () => {
    window.close();
  };

  React.useEffect(() => {
    if(isTruck){
      fetchTruckData();
    } else {
      fetchSellerData();
    }
  }, [])
  
  
  return (
    <Layout>
    <HeaderScreen title={isTruck ? `การจ่ายสารเคมี - [${dateToHumanString(_.get(truckData, 'created', '-'))}] ${_.get(truckData, 'vehicle_license_plate', '-')}`: `การจ่ายสารเคมี - ${_.get(sellerData, 'name', '')}`} style={{ backgroundColor : SECONDARY_COLOR, color : WHITE_COLOR, fontSize : 18, fontWeight: 'bolder' }} onClick={handleClose}/>
    <ErrorMessages message={errorMessages} style ={{ margin : 16}}/>
      <Content
        style={{
          margin: 24,
          minHeight: 280,
          padding: "0px 24px",
      }}>
        {isTruck && 
          <Descriptions
            bordered
            column={{ xs: 1, sm: 2, md: 2, lg: 4, xl: 4, xxl: 4 }}
            items={items}
          />
        }
        {!disabled && <ColorButton
          type='primary'
          override={colorSuccess}
          icon={<PlusOutlined style={{ fontSize : 14 }}/>}
          onClick={() => setDispenseTarget('add')}
          style={{ float : 'right', marginBottom : 10, marginTop : 10}}
        >
          จ่ายสารเคมี
        </ColorButton>}
        {isTruck ? 
          truckData && <TSTable
            ref={tableRef}
            columns={columns}
            size={'small'}
            url={URL_TRUCK.CHEMICAL_TRANSFER}
            params={{ truck_transaction : _.get(params, 'id', '') }}
          /> :
          <TSTable
            ref={tableRef}
            columns={columns}
            size={'small'}
            url={URL_TRUCK.CHEMICAL_TRANSFER}
            params={{ supplier : _.get(params, 'id', '') }}
          />
        }
        <DispenseChemicalModal
          open={dispenseTarget ? true : false}
          onClose={() => {
            setDispenseTarget(null);
          }}
          onUpdate={() => {
            setDispenseTarget(null);
            refreshData();
          }}
          target={dispenseTarget}
          seller={isTruck ? _.get(truckData, 'supplier' , '') : _.get(params, 'id' , '')}
        />
      </Content>
    </Layout>
  )
}